import React, { DragEvent, FC, useCallback, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { THEME_PRIMARY, THEME_SMOKY, themeColor } from '../../../../styles/basics/colors';
import { ButtonIconTextWidthAuto } from '../../../../shared/common/Button/ButtonIconTextWidthAuto';
import { MATERIAL_ICON_ARROW_LEFT } from '../../../../shared/common/Button/Icon';
import { BUTTON_SIZE_SMALL, BUTTON_THEME_FULL_GREEN } from '../../../../shared/common/Button/themes';

const StMoveHereLine = styled.div`
    position: relative;
    height: 22px;
    display: flex;
    justify-content: space-between;
    padding-right: 30px;
`;

interface DividerProps {
    bold: boolean;
    highlight: boolean;
}

const StDivider = styled.div<DividerProps>`
    height: ${props => (props.bold ? '2px' : '1px')};
    margin-top: 10px;
    margin-bottom: 10px;
    width: 100%;
    background-color: ${props => (props.highlight ? themeColor(THEME_PRIMARY) : themeColor(THEME_SMOKY))};
`;

const StDropZone = styled.div`
    position: absolute;
    height: 70px;
    width: 100%;
    opacity: 0.5;
    top: -25px;
`;

const StButtonIconTextWidthAuto = styled(ButtonIconTextWidthAuto)`
    z-index: 1;
`;

type Props = {
    indexInList: number;
    isInSelectionMode: boolean;
    handleStatefulMove: (index: number) => void;
    handleStatelessMove: (currentProductRank: number, nextProductRank: number) => void;
};

const MoveHereLine: FC<Props> = ({ indexInList, isInSelectionMode, handleStatefulMove, handleStatelessMove }) => {
    const { t } = useTranslation();
    const [dragOver, setDragOver] = useState(false);

    const allowDrop = useCallback((event: DragEvent<HTMLDivElement>) => {
        if (event.dataTransfer.effectAllowed !== 'move') {
            return;
        }

        setDragOver(true);
        event.stopPropagation();
        event.preventDefault();
    }, []);

    const handleDrop = useCallback(
        (event: DragEvent<HTMLDivElement>) => {
            const movedProductRank = Number(event.dataTransfer.getData('text'));
            if (event.dataTransfer.effectAllowed !== 'move' || isNaN(movedProductRank)) {
                return;
            }

            const isMoveDown = movedProductRank < indexInList;
            const fixedIndex = isMoveDown ? indexInList - 1 : indexInList;

            handleStatelessMove(movedProductRank, fixedIndex);
            setDragOver(false);
        },
        [handleStatelessMove, indexInList]
    );

    return (
        <StMoveHereLine onDragLeave={() => setDragOver(false)} onDragOver={allowDrop} onDrop={handleDrop}>
            <StDropZone />
            <StDivider highlight={dragOver || isInSelectionMode} bold={dragOver} />
            {isInSelectionMode ? (
                <StButtonIconTextWidthAuto
                    dataDisplayname="ProductList-MoveHere-Button"
                    icon={MATERIAL_ICON_ARROW_LEFT}
                    onClick={() => handleStatefulMove(indexInList)}
                    theme={BUTTON_THEME_FULL_GREEN}
                    size={BUTTON_SIZE_SMALL}
                >
                    {t('marketing.productGroups.moveHere')}
                </StButtonIconTextWidthAuto>
            ) : null}
        </StMoveHereLine>
    );
};

export default MoveHereLine;
